import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

export default function CartDrawer() {
  const { items, updateQty, removeItem, subtotal, open, setOpen } = useCart();
  const navigate = useNavigate();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="bg-[#0a0a0a] border-l border-[#262626] text-white w-full sm:max-w-md p-0"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-5 border-b border-[#262626]">
          <SheetTitle className="font-display text-2xl tracking-wider text-white flex items-center justify-between">
            YOUR CART
            <button
              onClick={() => setOpen(false)}
              data-testid="close-cart-button"
              className="text-[#9ca3af] hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </SheetTitle>
        </SheetHeader>

        <div className="flex flex-col h-[calc(100vh-72px)]">
          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-10 text-center text-[#9ca3af]" data-testid="cart-empty-state">
                <p className="font-display text-xl tracking-wider text-white mb-2">EMPTY GARAGE</p>
                <p className="text-sm">Nothing in your cart yet. Go raise some hell in the shop.</p>
                <button
                  onClick={() => {
                    setOpen(false);
                    navigate("/shop");
                  }}
                  className="btn-primary mt-6"
                  data-testid="cart-shop-now-button"
                >
                  SHOP NOW
                </button>
              </div>
            ) : (
              <ul>
                {items.map((it, idx) => (
                  <li
                    key={`${it.product_id}-${idx}`}
                    className="px-6 py-5 border-b border-[#1a1a1a] flex gap-4"
                    data-testid={`cart-item-${idx}`}
                  >
                    {it.image && (
                      <img
                        src={it.image}
                        alt={it.name}
                        className="w-20 h-20 object-cover border border-[#262626]"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-display text-sm tracking-wider uppercase truncate">{it.name}</p>
                      {it.variant && (
                        <p className="text-xs text-[#9ca3af] mt-0.5">
                          {Object.entries(it.variant).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                        </p>
                      )}
                      <p className="text-[#c8102e] font-semibold mt-1">${it.price.toFixed(2)}</p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center border border-[#262626]">
                          <button
                            onClick={() => updateQty(idx, it.quantity - 1)}
                            className="p-2 hover:bg-[#1a1a1a]"
                            data-testid={`cart-item-${idx}-dec`}
                            aria-label="Decrease"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="px-3 text-sm">{it.quantity}</span>
                          <button
                            onClick={() => updateQty(idx, it.quantity + 1)}
                            className="p-2 hover:bg-[#1a1a1a]"
                            data-testid={`cart-item-${idx}-inc`}
                            aria-label="Increase"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <button
                          onClick={() => removeItem(idx)}
                          className="text-[#9ca3af] hover:text-[#c8102e]"
                          data-testid={`cart-item-${idx}-remove`}
                          aria-label="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {items.length > 0 && (
            <div className="border-t border-[#262626] p-6 space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-[#9ca3af]">Subtotal</span>
                <span className="font-semibold" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
              </div>
              <p className="text-xs text-[#6b7280]">Shipping calculated at checkout.</p>
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/checkout");
                }}
                className="btn-primary w-full justify-center"
                data-testid="cart-checkout-button"
              >
                CHECKOUT
              </button>
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/cart");
                }}
                className="btn-ghost w-full justify-center"
                data-testid="cart-view-button"
              >
                VIEW CART
              </button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
